import { Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AuthService, EmailConflictInfo, EmailConflictLocation } from '../../services/auth.service';

/**
 * Outcome the dialog returns to its caller via DynamicDialogRef.close(...).
 * <ul>
 *   <li><code>reassigned</code>: the email was successfully moved to the current user.
 *       The caller should clear the field-level validation error and retry the save.</li>
 *   <li><code>cancelled</code>: the user closed the dialog without reassigning. The
 *       caller should leave the validation error in place so the form does not submit.</li>
 * </ul>
 */
export type EmailConflictDialogResult = 'reassigned' | 'cancelled';

/**
 * Shown when a user-form save would collide with an email already in use. The dialog
 * explains who currently owns the email and offers a reassignment action ONLY if:
 *   - the calling user is a Global or Principal Account Admin (server-checked), AND
 *   - the previous owner is an orphan AspNet record or an inactive ProximityUser.
 *
 * Active-user collisions are read-only. The admin's two real choices are surfaced inline
 * as guidance: inactivate the current owner (via the deep link in the location panel —
 * which then makes the InactiveUser reassign path eligible) or choose a different email
 * for the user being edited. Clearing the active owner's email is deliberately NOT offered
 * because certain roles (e.g. PrincipalAccountAdmin) require an email for login and
 * password-reset flows.
 */
@Component({
  selector: 'prox-email-conflict-dialog',
  standalone: false,
  template: `
    <div class="space-y-3">
      <div>
        <div class="text-l mb-2 font-bold">
          The email address "{{ conflict?.email }}" is already in use.
        </div>
        <div class="text-sm">It is currently assigned to:</div>
      </div>

      <div class="space-y-1 border border-gray-300 bg-gray-50 p-3 text-sm">
        <div><span class="font-semibold">Username:</span> {{ conflict?.username }}</div>
        <div *ngIf="conflict?.fullName">
          <span class="font-semibold">Name:</span> {{ conflict?.fullName }}
        </div>
        <div *ngIf="conflict?.roleName">
          <span class="font-semibold">Role:</span> {{ conflict?.roleName }}
        </div>
        <div *ngIf="conflict?.activeStatus">
          <span class="font-semibold">Status:</span> {{ conflict?.activeStatus }}
        </div>
        <div>
          <span class="font-semibold">Record type:</span> {{ conflict?.kindLabel }}
        </div>
      </div>

      <div *ngIf="showLocationSection()" class="space-y-1 border border-gray-300 bg-gray-50 p-3 text-sm">
        <div class="font-semibold">Where to find this user:</div>
        <ng-container *ngIf="conflict?.locationVisibleToCaller; else cannotSeeLocation">
          <ng-container *ngIf="(conflict?.locations || []).length > 0; else noMemberships">
            <div *ngFor="let loc of conflict?.locations">
              <span class="text-gray-600">{{ membershipLabel(loc) }}:</span>
              <a [routerLink]="deepLink(loc)" (click)="openLocation()" class="bluelink ml-1">
                {{ loc.name }}
              </a>
            </div>
          </ng-container>
          <ng-template #noMemberships>
            <div class="italic opacity-70">No active memberships found.</div>
          </ng-template>
        </ng-container>
        <ng-template #cannotSeeLocation>
          <div>Belongs to a different account — contact a Global Administrator.</div>
        </ng-template>
      </div>

      <ng-container *ngIf="conflict?.canReassign; else cannotReassign">
        <div *ngIf="conflict?.kind === 'OrphanAspNet'" class="text-sm">
          Reassigning will <span class="font-semibold">permanently delete</span> this orphan record
          and move the email to the current user.
        </div>
        <div *ngIf="conflict?.kind === 'InactiveUser'" class="text-sm">
          Reassigning will <span class="font-semibold">clear the email</span> from the inactive user
          and move it to the current user. The inactive user's other details are preserved.
        </div>
        <div class="text-xs italic opacity-70">This action is permanent and cannot be undone.</div>
      </ng-container>

      <ng-template #cannotReassign>
        <div class="text-sm">
          {{ conflict?.reassignBlockedReason }}
        </div>
      </ng-template>

      <div *ngIf="errorMessage" class="text-sm font-semibold text-red-600">
        {{ errorMessage }}
      </div>

      <div class="mt-3 flex justify-end gap-2">
        <button
          *ngIf="conflict?.canReassign"
          (click)="reassign()"
          [disabled]="busy"
          pButton
          type="button"
          icon="pi pi-arrow-right"
          label="Reassign email and save"
          class="p-button-raised p-button-primary"
        ></button>
        <button
          (click)="cancel()"
          [disabled]="busy"
          pButton
          type="button"
          icon="pi pi-times"
          label="Cancel"
          class="p-button-raised p-button-secondary"
        ></button>
      </div>
    </div>
  `,
})
export class EmailConflictDialogComponent {
  conflict?: EmailConflictInfo;
  /** Id of the user receiving the email (the form's proximityUser.id). */
  toUserId!: string;
  busy = false;
  errorMessage?: string;

  constructor(
    dialogConfig: DynamicDialogConfig,
    private ref: DynamicDialogRef,
    private authService: AuthService,
  ) {
    const data = dialogConfig.data ?? {};
    this.conflict = data.conflict;
    this.toUserId = data.toUserId;
  }

  /** Whether to render the "Where to find this user" panel at all. Hidden for orphan
   * AspNet records since they have no ProximityUser memberships to point at. */
  showLocationSection(): boolean {
    return !!this.conflict && this.conflict.kind !== 'OrphanAspNet';
  }

  /** Short human label for the membership type, used as the line prefix. */
  membershipLabel(loc: EmailConflictLocation): string {
    switch (loc.membershipType) {
      case 'AccountUser':    return 'Account user at';
      case 'AccountAdmin':   return 'Account admin at';
      case 'SupplierAdmin':  return 'Supplier admin at';
      case 'GlobalAdmin':    return 'Global administrator';
    }
  }

  /** Builds the deep-link routerLink array for the membership's edit form. */
  deepLink(loc: EmailConflictLocation): any[] {
    switch (loc.membershipType) {
      case 'AccountUser':
        return ['/account', 'accounts', loc.parentId, 'manage', 'account-users', loc.membershipId];
      case 'AccountAdmin':
        return ['/account', 'accounts', loc.parentId, 'manage', 'account-administrators', loc.membershipId];
      case 'SupplierAdmin':
        return ['/supplier', 'suppliers', loc.parentId, 'manage', 'supplier-administrators', loc.membershipId];
      case 'GlobalAdmin':
        return ['/global', 'global-admins', loc.membershipId];
    }
  }

  /** Close the dialog before navigating so the routed page isn't trapped behind the modal. */
  openLocation() {
    this.ref.close('cancelled' as EmailConflictDialogResult);
  }

  async reassign() {
    if (!this.conflict || !this.conflict.canReassign) return;
    this.busy = true;
    this.errorMessage = undefined;
    const res = await this.authService.reassignEmail(this.conflict.userId, this.toUserId, this.conflict.email);
    this.busy = false;
    if (res.success) {
      this.ref.close('reassigned' as EmailConflictDialogResult);
    } else {
      this.errorMessage = res.message ?? 'Reassignment failed.';
    }
  }

  cancel() {
    this.ref.close('cancelled' as EmailConflictDialogResult);
  }
}
